import React from "react";
import SettingsTab from "../SettingsTab";
import {
  Spinner,
  Button,
  Intent,
  FormGroup,
  Callout,
  InputGroup, Icon
} from "@blueprintjs/core";
import styles from "./CustomDomainSettings.module.scss";
import { Toolbar, ToolbarGroup } from "../../../molecules/Toolbar";
import SettingsForm from "../SettingsForm";
import FormInfo from "../../../atoms/FormInfo/FormInfo";
import FormHeading from "../../../atoms/FormHeading/FormHeading";
import { useChangeCustomDomain } from "./hook";

export default function CustomDomainSettings() {
  const {
    videoDomainUrl,
    setVideoDomainUrl,
    brandDomainUrl,
    setBrandDomainUrl,
    isSaving,
    error,
    saveChanges,
    cancelChanges,
    loading,
    isSaveEnabled,
    account
  } = useChangeCustomDomain();


  if (loading) {
    return (
      <SettingsTab centered>
        <Spinner />
      </SettingsTab>
    );
  }

  return (
    <SettingsTab>
      <Toolbar title="Settings">
        <ToolbarGroup>
          <Button
            text="Cancel"
            style={{ marginRight: "10px" }}
            disabled={!isSaveEnabled}
            onClick={cancelChanges}
          />
          <Button
            text="Save"
            intent={Intent.PRIMARY}
            loading={isSaving}
            disabled={!isSaveEnabled}
            onClick={saveChanges}
          />
        </ToolbarGroup>
      </Toolbar>

      <SettingsForm className={styles.customDomainSettingsForm}>
        {error && (
          <Callout
            icon="error"
            intent={Intent.DANGER}
            title="We couldn't save your settings."
            style={{ marginBottom: '1em' }}
          >
            {error.message}
          </Callout>
        )}

        <div className={styles.customDomainSettingsFormWrapper}>

          <FormHeading weight="600" size={16}>
            Custom Domain
          </FormHeading>

          <FormInfo>
            By default, your videos and opportunities are hosted at <kbd className={styles.kbd}>videosquirrel.app</kbd>. You can use your own
            URL instead by setting up a custom domain.
          </FormInfo>

          <FormInfo className={styles.mt2}>
            Before you start, we strongly recommend reading our support article about custom domains...<br /><br />
            <a href="https://help.videosquirrel.app/en/articles/4999511-setting-up-a-custom-domain-for-videos-and-opportunities" target="_blank">
              <Icon icon="help" style={{ marginRight: '1em' }} />
              Learn more about setting up a custom domain
            </a>
          </FormInfo>

        </div>

        <div className={styles.customDomainSectionWrapper}>
          <div className={styles.customDomainSection}>
            <FormHeading weight="600" size={16}>
              Custom Brand Page &amp; Opportunities Domain
            </FormHeading>

            {account && account.brandDomainUrl !== '' && (
              <Callout style={{ margin: '1em 0' }} intent={Intent.PRIMARY} icon="tick">
                You've added your custom domain. Please make sure you've configured your <kbd
                className={styles.kbd}>CNAME</kbd> record properly for changes to take effect.
              </Callout>
            )}

            <FormInfo>
              Start by providing us with your custom domain so we know where your visitors will be coming from.
            </FormInfo>

            <FormGroup label="Custom Domain">
              <InputGroup
                type="text"
                autoCapitalize="none"
                autoCorrect="false"
                value={brandDomainUrl}
                placeholder="influencers.herschelsupplyco.com"
                onChange={setBrandDomainUrl}
              />
            </FormGroup>

            <FormInfo>
              Next, add the following <kbd className={styles.kbd}>CNAME</kbd> record to your domain's DNS settings...
            </FormInfo>

            <FormGroup label="">
              <InputGroup type="text" value={process.env.REACT_APP_CUSTOM_BRAND_DOMAIN} disabled />
            </FormGroup>

            <FormInfo>
              Finally, make sure you follow steps in our <a href="https://help.videosquirrel.app/en/articles/4999511-setting-up-a-custom-domain-for-videos-and-opportunities" target="_blank">help article</a> for setting up SSL
              with your custom domain. Once you click <strong>Save</strong> above and add the <kbd className={styles.kbd}>CNAME</kbd> record, it can take up to 72 hours for your custom domain to work.
            </FormInfo>
          </div>

          <div className={styles.customDomainSection}>
            <FormHeading weight="600" size={16}>
              Custom Videos Domain
            </FormHeading>

            {account && account.domainUrl !== '' && (
              <Callout style={{ margin: '1em 0' }} intent={Intent.PRIMARY} icon="tick">
                You've added your custom domain. Please make sure you've configured your <kbd
                className={styles.kbd}>CNAME</kbd> record properly for changes to take effect.
              </Callout>
            )}

            <FormInfo>
              Start by providing us with your custom domain so we know where your visitors will be coming from.
            </FormInfo>

            <FormGroup label="Custom Domain">
              <InputGroup
                type="text"
                autoCapitalize="none"
                autoCorrect="false"
                value={videoDomainUrl}
                placeholder="videos.herschelsupplyco.com"
                onChange={setVideoDomainUrl}
              />
            </FormGroup>

            <FormInfo>
              Next, add the following <kbd className={styles.kbd}>CNAME</kbd> record to your domain's DNS settings...
            </FormInfo>

            <FormGroup label="">
              <InputGroup type="text" value={process.env.REACT_APP_CUSTOM_VIDEO_DOMAIN} disabled />
            </FormGroup>

            <FormInfo>
              Finally, make sure you follow steps in our <a href="https://help.videosquirrel.app/en/articles/4999511-setting-up-a-custom-domain-for-videos-and-opportunities" target="_blank">help article</a> for setting up SSL
              with your custom domain. Once you click <strong>Save</strong> above and add the <kbd className={styles.kbd}>CNAME</kbd> record, it can take up to 72 hours for your custom domain to work.
            </FormInfo>
          </div>
        </div>
      </SettingsForm>
    </SettingsTab>
  );
}
